import { useState, useEffect, useRef } from "react";
import { toJS } from "mobx";
import { useNavigate } from "react-router-dom";
import CurriculumStore from "../stores/CurriculumStore";

export default function useCurriculum(curriculumId) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [curriculum, setCurriculum] = useState();
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const loadedCurriculumIdRef = useRef(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    // Already loaded or currently fetching
    if (loadedCurriculumIdRef.current === curriculumId || fetchingRef.current) return;

    const matchingCurriculum = CurriculumStore?.curriculaById[curriculumId];
    if (matchingCurriculum?.id) {
      setCurriculum(toJS(matchingCurriculum));
      loadedCurriculumIdRef.current = curriculumId;
      setLoading(false);
    } else {
      if (curriculumId) {
        setCurriculum(undefined);
        loadedCurriculumIdRef.current = null;
        fetchingRef.current = true;
        (async () => {
          setLoading(true);
          setError(null);
          setNotFound(false);
          try {
            // Wait for a single, completed initialization before deciding the
            // curriculum is missing. Without this the cold-load race redirected
            // valid deep links back to the curriculum list.
            await CurriculumStore.ensureInitialized();
            const curriculum = CurriculumStore?.curriculaById[curriculumId];
            if (!curriculum?.id) {
              setNotFound(true);
              loadedCurriculumIdRef.current = curriculumId;
              navigate("/curriculum", { replace: true });
            } else {
              setCurriculum(toJS(curriculum));
              loadedCurriculumIdRef.current = curriculumId;
            }
          } catch (err) {
            // A failed fetch is not a missing curriculum - surface it instead of
            // silently bouncing the user back to the list.
            console.error(err);
            setError(err);
          } finally {
            setLoading(false);
            fetchingRef.current = false;
          }
        })();
      }
    }
  }, [curriculumId, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lets an error state retry the load without a full page refresh.
  const reload = () => {
    loadedCurriculumIdRef.current = null;
    fetchingRef.current = false;
    setError(null);
    setNotFound(false);
    setReloadNonce(n => n + 1);
  };

  return { curriculum: curriculum || {}, loading, error, notFound, reload };
}
